import React, { createContext, useContext, useState, useEffect } from 'react';
import fileSystemService from '../services/FileSystemService';

const DraftContext = createContext();

export function DraftProvider({ children }) {
  // Info fields
  const [teamName, setTeamName] = useState('');
  const [tournament, setTournament] = useState('');
  const [stage, setStage] = useState('');
  const [draftDate, setDraftDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  // Both pick modes
  const [scenariosFirstPick, setScenariosFirstPick] = useState([
    { id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }
  ]);
  const [scenariosSecondPick, setScenariosSecondPick] = useState([
    { id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [downloadedDrafts, setDownloadedDrafts] = useState([]);
  const [isFileSystemApiSupported, setIsFileSystemApiSupported] = useState(false);
  const [selectedFolderPath, setSelectedFolderPath] = useState(null);
  const [originalDraftData, setOriginalDraftData] = useState(null);

  // Check if File System Access API is supported
  useEffect(() => {
    setIsFileSystemApiSupported(!!window.showDirectoryPicker);
    // Load the saved folder path
    const savedPath = fileSystemService.getSavedFolderPath();
    if (savedPath) {
      setSelectedFolderPath(savedPath);
    }
    
    // Pre-cache any existing drafts in tracking
    preCacheExistingDrafts();
  }, []);
  
  // Pre-cache existing drafts from tracking
  const preCacheExistingDrafts = () => {
    try {
      const tracking = fileSystemService.getDraftTracking();
      if (!tracking) return;
      
      // Check if each draft in tracking is already cached
      Object.keys(tracking).forEach(draftId => {
        const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
        if (!cachedDraftJson) {
          console.log(`Draft ${draftId} not cached yet`);
        }
      });
    } catch (error) {
      console.error('Error pre-caching drafts:', error);
    }
  };

  // Add scenario for a pick mode
  const addScenario = (pickMode) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    const maxId = scenarios.length > 0 ? Math.max(...scenarios.map(s => s.id)) : -1;
    const newScenario = { id: maxId + 1, title: `Scenario ${maxId + 2}`, picks: Array(24).fill(null) };
    setScenarios([...scenarios, newScenario]);
    setHasUnsavedChanges(true);
  };

  // Duplicate scenario for a pick mode
  const duplicateScenario = (pickMode, scenarioId) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    const scenarioToDuplicate = scenarios.find(s => s.id === scenarioId);
    if (!scenarioToDuplicate) return;
    const maxId = Math.max(...scenarios.map(s => s.id));
    const newScenario = {
      id: maxId + 1,
      title: `${scenarioToDuplicate.title} (Copy)`,
      picks: JSON.parse(JSON.stringify(scenarioToDuplicate.picks))
    };
    setScenarios([...scenarios, newScenario]);
    setHasUnsavedChanges(true);
  };

  // Delete scenario for a pick mode
  const deleteScenario = (pickMode, scenarioId) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    if (scenarios.length <= 1) return;
    setScenarios(scenarios.filter(s => s.id !== scenarioId));
    setHasUnsavedChanges(true);
  };

  // Update scenario for a pick mode
  const updateScenario = (pickMode, scenarioId, updates) => {
    const setScenarios = pickMode === 'first' ? setScenariosFirstPick : setScenariosSecondPick;
    const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    setScenarios(scenarios.map(s => s.id === scenarioId ? { ...s, ...updates } : s));
    setHasUnsavedChanges(true);
  };

  // Function to detect if current draft has changes compared to original
  const hasSignificantChanges = () => {
    if (!originalDraftData || !currentDraftId) return false;
    
    const currentData = {
      scenariosFirstPick,
      scenariosSecondPick,
      teamName,
      tournament,
      stage,
      date: draftDate
    };
    
    // Check for changes in basic fields
    if (originalDraftData.teamName !== currentData.teamName ||
        originalDraftData.tournament !== currentData.tournament ||
        originalDraftData.stage !== currentData.stage ||
        originalDraftData.date !== currentData.date) {
      return true;
    }
    
    // Check for changes in scenarios (deep comparison)
    const originalFirstPick = JSON.stringify(originalDraftData.scenariosFirstPick || []);
    const currentFirstPick = JSON.stringify(currentData.scenariosFirstPick || []);
    const originalSecondPick = JSON.stringify(originalDraftData.scenariosSecondPick || []);
    const currentSecondPick = JSON.stringify(currentData.scenariosSecondPick || []);
    
    if (originalFirstPick !== currentFirstPick || originalSecondPick !== currentSecondPick) {
      return true;
    }
    
    return false;
  };

  // Save draft (both pick modes and info fields)
  const saveDraft = async (draftInfo = {}) => {
    setLoading(true);
    setError(null);
    try {
      // If we're editing an existing draft and there are significant changes, create a new draft
      const shouldCreateNew = currentDraftId && hasSignificantChanges();
      
      const draftData = {
        id: shouldCreateNew ? null : currentDraftId, // null ID forces creation of new draft
        scenariosFirstPick,
        scenariosSecondPick,
        teamName,
        tournament,
        stage,
        title: draftInfo.title || `${teamName || 'Untitled'} - ${tournament || 'Draft'}`,
        date: draftDate,
        ...draftInfo
      };
      
      const savedId = await fileSystemService.saveDraft(draftData);
      setCurrentDraftId(savedId);
      setHasUnsavedChanges(false);
      
      // Update original draft data to current state if we created a new draft
      if (shouldCreateNew) {
        setOriginalDraftData({
          scenariosFirstPick,
          scenariosSecondPick,
          teamName,
          tournament,
          stage,
          date: draftDate
        });
        console.log('Created new draft due to changes. New ID:', savedId);
      }
      
      setLoading(false);
      return savedId;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Load draft (both pick modes and info fields)
  const loadDraft = async (draftId, filename, skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true, draftId, filename };
    }

    setLoading(true);
    setError(null);
    try {
      console.log(`Loading draft: ${draftId}, filename: ${filename}`);
      
      // Try to get draft data from cache first
      const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
      if (cachedDraftJson) {
        try {
          const cachedDraft = JSON.parse(cachedDraftJson);
          console.log('Found cached draft data:', draftId);
          loadDraftData(cachedDraft);
          setLoading(false);
          return cachedDraft;
        } catch (cacheError) {
          console.warn('Could not load cached draft, falling back to file', cacheError);
        }
      }
      
      // If no cache, try to load from file system service
      const draftData = await fileSystemService.getDraft(draftId, filename);
      if (!draftData) throw new Error('Draft not found');
      
      // Load draft data
      loadDraftData(draftData);
      
      // Cache the draft data for future quick loading
      try {
        localStorage.setItem(`draft_cache_${draftId}`, JSON.stringify(draftData));
        console.log('Draft cached from load:', draftId);
      } catch (cacheError) {
        console.warn('Could not cache draft data', cacheError);
      }
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Helper function to load draft data into state
  const loadDraftData = (draftData) => {
    const firstPick = draftData.scenariosFirstPick || [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }];
    const secondPick = draftData.scenariosSecondPick || [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }];
    const teamName = draftData.teamName || '';
    const tournament = draftData.tournament || '';
    const stage = draftData.stage || '';
    const date = draftData.date || (new Date().toISOString().slice(0, 10));
    
    setScenariosFirstPick(firstPick);
    setScenariosSecondPick(secondPick);
    setTeamName(teamName);
    setTournament(tournament);
    setStage(stage);
    setDraftDate(date);
    setCurrentDraftId(draftData.id);
    setHasUnsavedChanges(false);
    
    // Store original draft data for change detection
    setOriginalDraftData({
      scenariosFirstPick: JSON.parse(JSON.stringify(firstPick)), // Deep copy
      scenariosSecondPick: JSON.parse(JSON.stringify(secondPick)), // Deep copy
      teamName,
      tournament,
      stage,
      date
    });
  };

  // Load draft directly from file handle
  const loadDraftFromHandle = async (fileHandle) => {
    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFromHandle(fileHandle);
      if (!draftData) throw new Error('Draft not found');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Scan for drafts in the selected folder
  const scanDownloadsForDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDownloadsForDrafts();
      setDownloadedDrafts(drafts);
      
      // Update the selected folder path
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) {
        setSelectedFolderPath(savedPath);
      }
      
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Try to scan the default Downloads folder
  const scanDefaultDownloadsFolder = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDefaultDownloadsFolder();
      setDownloadedDrafts(drafts);
      
      // Update the selected folder path
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) {
        setSelectedFolderPath(savedPath);
      }
      
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Import a draft from file
  const importDraft = async (skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }

    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFile();
      if (!draftData) throw new Error('Failed to import draft');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Import draft data directly (for drag-and-drop)
  const importDraftData = async (draftData, skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return new Promise((resolve, reject) => {
        // Return a promise that will be resolved by the confirmation dialog
        resolve({ needsConfirmation: true, draftData });
      });
    }

    setLoading(true);
    setError(null);
    try {
      if (!draftData) throw new Error('No draft data provided');
      
      // Load draft data
      loadDraftData(draftData);
      
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Get all drafts
  const getDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = fileSystemService.getDrafts();
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Create a new draft (reset to initial state)
  const newDraft = (skipConfirmation = false) => {
    // Check for unsaved changes and show confirmation if needed
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }

    // Reset to initial state
    setScenariosFirstPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
    setScenariosSecondPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
    setTeamName('');
    setTournament('');
    setStage('');
    setDraftDate(new Date().toISOString().slice(0, 10));
    setCurrentDraftId(null);
    setHasUnsavedChanges(false);
    setOriginalDraftData(null); // Clear original data when creating new draft
    return { success: true };
  };

  // Delete a draft
  const deleteDraft = async (draftId) => {
    setLoading(true);
    setError(null);
    try {
      const success = fileSystemService.deleteDraft(draftId);
      setLoading(false);
      if (currentDraftId === draftId) {
        // Reset to initial state
        setScenariosFirstPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
        setScenariosSecondPick([{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }]);
        setTeamName('');
        setTournament('');
        setStage('');
        setDraftDate(new Date().toISOString().slice(0, 10));
        setCurrentDraftId(null);
        setHasUnsavedChanges(false);
      }
      return success;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  const value = {
    scenariosFirstPick,
    scenariosSecondPick,
    addScenario,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    saveDraft,
    loadDraft,
    loadDraftFromHandle,
    importDraft,
    importDraftData,
    getDrafts,
    scanDownloadsForDrafts,
    scanDefaultDownloadsFolder,
    downloadedDrafts,
    isFileSystemApiSupported,
    newDraft,
    deleteDraft,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    draftDate, setDraftDate,
    hasUnsavedChanges,
    hasSignificantChanges,
    currentDraftId,
    loading,
    error,
    selectedFolderPath
  };

  return (
    <DraftContext.Provider value={value}>
      {children}
    </DraftContext.Provider>
  );
}

export function useDraft() {
  return useContext(DraftContext);
}
